import { RiskLevel } from './types';

// Shared option lists for cycle metadata. Kept in sync with what the submit
// metadata form (ReviewDashboard.tsx SubmitFileMetadataModal) accepts so
// admin rename / fork dialogs offer the same vocabulary instead of free
// text.

export const PURPOSE_OPTIONS = [
  'Toolkit', 'Presentation', 'Brochure', 'Social', 'Press Release',
  'Email', 'Newsletter', 'Website', 'Knowledge Hub', 'Other'
];

export const AUDIENCE_OPTIONS = [
  'HCP', 'Public', 'Patient', 'B2B Partner', 'Participant', 'Internal', 'All'
];

// ─────────────────────────────────────────────────────────────────────
// O-2 / O-7 (joint critical review, 2026-08-14) — post-approval
// automation status.
//
// The pipeline APPENDS its steps to cycle.automation on every run, so
// after a re-run the array holds several entries per step. Anything
// reading "did this succeed?" must therefore look at the LATEST entry per
// step — a naive `.some(a => a.status === 'failed')` over the whole array
// would keep showing red forever after a re-run had fixed the problem.
// ─────────────────────────────────────────────────────────────────────

export type AutomationEntry = {
  step: string;
  status: 'ok' | 'failed' | 'skipped';
  detail?: string;
  error?: string;
  at?: string;
};

/** Human labels for the pipeline's step keys. */
export const AUTOMATION_STEP_LABELS: Record<string, string> = {
  archive: 'Archive copy',
  finalOrgMove: 'Approved-content move',
  workingFileMove: 'Editable source',
  renderDistributable: 'PDF rendition',
  channelHandler: 'Channel publish',
  notify: 'Notification'
};

export type AutomationSummary = {
  ran: boolean;
  steps: AutomationEntry[];   // latest entry per step, newest run wins
  failed: AutomationEntry[];
  hasFailure: boolean;
};

/**
 * Reduce a cycle's automation trail to its CURRENT state: one entry per
 * step, the most recent wins. Order is preserved by first appearance so
 * the pipeline's step order still reads top-to-bottom.
 */
export function automationSummary(automation?: AutomationEntry[] | null): AutomationSummary {
  const all = automation || [];
  if (all.length === 0) return { ran: false, steps: [], failed: [], hasFailure: false };

  const order: string[] = [];
  const latest = new Map<string, AutomationEntry>();
  for (const entry of all) {
    if (!latest.has(entry.step)) order.push(entry.step);
    const prev = latest.get(entry.step);
    // Entries arrive in append order; prefer the later timestamp but fall
    // back to array position when `at` is missing or equal.
    if (!prev) { latest.set(entry.step, entry); continue; }
    const prevAt = prev.at ? new Date(prev.at).getTime() : 0;
    const nextAt = entry.at ? new Date(entry.at).getTime() : 0;
    if (nextAt >= prevAt) latest.set(entry.step, entry);
  }
  const steps = order.map(s => latest.get(s)!).filter(Boolean);
  const failed = steps.filter(s => s.status === 'failed');
  return { ran: true, steps, failed, hasFailure: failed.length > 0 };
}

// Risk levels a reviewer may set on a finding. Moved here from
// ReviewInterface on 2026-08-26 when the edit form was extracted for the
// decision maker's card — two copies of this list is one copy too many, and
// the select is now rendered from three places.
export const RISK_OPTIONS: { value: RiskLevel; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'major', label: 'Major' },
  { value: 'minor', label: 'Minor' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'none', label: 'No risk' }
];
